import "../../css/blocks/contentslider.css";
import KeenSlider from "keen-slider";
import type { KeenSliderInstance } from "keen-slider";

// Store active slider instances for cleanup
const sliderInstances = new WeakMap<HTMLElement, KeenSliderInstance>();

interface SliderElements {
  container: HTMLElement;
  prevButton: HTMLButtonElement | null;
  nextButton: HTMLButtonElement | null;
  navButtons: NodeListOf<HTMLButtonElement>;
  navSelect: HTMLSelectElement | null;
}

function initContentSlider(block: Element): void {
  const sliderContainer = block.querySelector(".keen-slider") as HTMLElement;

  if (!sliderContainer) return;

  // Clean up existing instance if any
  const existingSlider = sliderInstances.get(sliderContainer);
  if (existingSlider) {
    existingSlider.destroy();
  }

  const elements: SliderElements = {
    container: sliderContainer,
    prevButton: block.querySelector(".slider-prev"),
    nextButton: block.querySelector(".slider-next"),
    navButtons: block.querySelectorAll(".slider-nav-button"),
    navSelect: block.querySelector(".slider-nav-select"),
  };

  // Initialize Keen Slider
  const slider: KeenSliderInstance = new KeenSlider(sliderContainer, {
    loop: false,
    slides: {
      perView: 1,
      spacing: 0,
    },
    created: (slider) => {
      updateNavigation(slider, elements);
    },
    slideChanged: (slider) => {
      updateNavigation(slider, elements);
    },
  });

  // Store instance for cleanup
  sliderInstances.set(sliderContainer, slider);

  // Setup navigation controls
  setupNavigationControls(slider, elements);
}

function updateNavigation(
  slider: KeenSliderInstance,
  elements: SliderElements,
): void {
  const currentSlide = slider.track.details.rel;
  const totalSlides = slider.track.details.slides.length;

  // Update arrow buttons state
  if (elements.prevButton) {
    elements.prevButton.disabled = currentSlide === 0;
  }
  if (elements.nextButton) {
    elements.nextButton.disabled = currentSlide === totalSlides - 1;
  }

  // Update desktop navigation buttons
  elements.navButtons.forEach((button, index) => {
    if (index === currentSlide) {
      // Active button styling
      button.classList.remove(
        "border-certlex-blue-light",
        "text-certlex-blue-light",
        "hover:border-certlex-blue",
        "hover:text-certlex-blue",
      );
      button.classList.add(
        "border-certlex-blue",
        "text-certlex-blue",
        "hover:border-certlex-blue-light",
        "hover:text-certlex-blue-light",
      );
    } else {
      // Inactive button styling
      button.classList.remove(
        "border-certlex-blue",
        "text-certlex-blue",
        "hover:border-certlex-blue-light",
        "hover:text-certlex-blue-light",
      );
      button.classList.add(
        "border-certlex-blue-light",
        "text-certlex-blue-light",
        "hover:border-certlex-blue",
        "hover:text-certlex-blue",
      );
    }
  });

  // Update mobile select dropdown
  if (elements.navSelect) {
    elements.navSelect.value = currentSlide.toString();
  }
}

function setupNavigationControls(
  slider: KeenSliderInstance,
  elements: SliderElements,
): void {
  // Arrow navigation
  elements.prevButton?.addEventListener("click", () => {
    slider.prev();
  });

  elements.nextButton?.addEventListener("click", () => {
    slider.next();
  });

  // Desktop button navigation
  elements.navButtons.forEach((button, index) => {
    button.addEventListener("click", () => {
      slider.moveToIdx(index);
    });
  });

  // Mobile select navigation
  elements.navSelect?.addEventListener("change", (e) => {
    const target = e.target as HTMLSelectElement;
    slider.moveToIdx(parseInt(target.value, 10));
  });
}

function initAllSliders(): void {
  const contentsliderBlocks = document.querySelectorAll(".contentslider");

  if (contentsliderBlocks.length) {
    contentsliderBlocks.forEach((block) => {
      initContentSlider(block);
    });
  }
}

// Check if we're in the WordPress block editor
const isBlockEditor = () => {
  return !!(
    window.wp &&
    window.wp.blocks &&
    window.wp.element &&
    document.body.classList.contains("block-editor-page")
  );
};

// Initialize based on context
if (isBlockEditor()) {
  // We're in the block editor - use WordPress APIs
  if (window.wp && window.wp.domReady) {
    // Initial load
    window.wp.domReady(() => {
      initAllSliders();
    });

    // Re-initialize when blocks are updated
    if (window.wp.data && window.wp.data.subscribe) {
      let previousBlocks: string = "";

      window.wp.data.subscribe(() => {
        // Check if we have the block editor store
        const blockEditorStore = window.wp.data.select("core/block-editor");
        if (!blockEditorStore) return;

        // Get current blocks
        const blocks = blockEditorStore.getBlocks();
        const blocksString = JSON.stringify(blocks);

        // Only re-init if blocks actually changed
        if (blocksString !== previousBlocks) {
          previousBlocks = blocksString;

          // Wait for DOM to update
          setTimeout(() => {
            initAllSliders();
          }, 100);
        }
      });
    }
  }
} else {
  // Frontend - use standard window load
  window.addEventListener("load", () => {
    initAllSliders();
  });
}